import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);

export const DAY_WIDTH = 28; // 하루당 픽셀 너비

/**
 * 날짜 포맷 변환
 */
export function formatDate(date: string | Dayjs, format = 'YYYY-MM-DD'): string {
  return dayjs(date).format(format);
}

/**
 * 두 날짜 사이의 일수 계산
 */
export function daysBetween(start: string | Dayjs, end: string | Dayjs): number {
  return dayjs(end).diff(dayjs(start), 'day') + 1;
}

/**
 * 프로젝트 시작일로부터의 오프셋 계산 (픽셀)
 */
export function getDateOffset(date: string | Dayjs, projectStart: Dayjs): number {
  const days = dayjs(date).diff(projectStart, 'day');
  return days * DAY_WIDTH;
}

/**
 * 기간의 너비 계산 (픽셀)
 */
export function getBarWidth(startDate: string, endDate: string): number {
  const days = daysBetween(startDate, endDate);
  return days * DAY_WIDTH;
}

/**
 * 월 이름 반환
 */
export function getMonthName(date: Dayjs): string {
  return `${date.year()}년 ${date.month() + 1}월`;
}

/**
 * 주말인지 확인
 */
export function isWeekend(date: Dayjs): boolean {
  const day = date.day();
  return day === 0 || day === 6;
}

/**
 * 오늘인지 확인
 */
export function isToday(date: Dayjs): boolean {
  return date.isSame(dayjs(), 'day');
}

/**
 * 상태에 따른 CSS 클래스 반환
 */
export function getStatusClass(status: string): string {
  const classes: Record<string, string> = {
    '시작전': 'before',
    '진행중': 'progress',
    '컨펌중': 'confirm',
    '완료': 'complete'
  };
  return classes[status] || 'before';
}

/**
 * 프로젝트 기간 내의 모든 날짜 생성
 */
export function generateProjectDates(projectStart: Dayjs, projectEnd: Dayjs): Dayjs[] {
  const dates: Dayjs[] = [];
  let current = projectStart;

  while (current.isBefore(projectEnd) || current.isSame(projectEnd, 'day')) {
    dates.push(current);
    current = current.add(1, 'day');
  }

  return dates;
}

/**
 * 월별로 그룹화된 날짜
 */
export interface MonthGroup {
  name: string;
  days: Dayjs[];
}

export function getDatesByMonth(projectStart: Dayjs, projectEnd: Dayjs): Record<string, MonthGroup> {
  const dates = generateProjectDates(projectStart, projectEnd);
  const months: Record<string, MonthGroup> = {};

  dates.forEach(date => {
    const monthKey = date.format('YYYY-MM');
    if (!months[monthKey]) {
      months[monthKey] = {
        name: getMonthName(date),
        days: []
      };
    }
    months[monthKey].days.push(date);
  });

  return months;
}

/**
 * 주차별로 그룹화된 날짜 (프로젝트 시작일 기준 1주차)
 */
export interface WeekGroup {
  week: number;
  month: number;
  days: Dayjs[];
}

export function getDatesByWeek(projectStart: Dayjs, projectEnd: Dayjs): Record<string, WeekGroup> {
  const dates = generateProjectDates(projectStart, projectEnd);
  const weeks: Record<string, WeekGroup> = {};

  dates.forEach(date => {
    const daysSinceStart = date.diff(projectStart, 'day');
    const weekNum = Math.floor(daysSinceStart / 7) + 1;
    const weekKey = `W${weekNum}`;

    if (!weeks[weekKey]) {
      weeks[weekKey] = {
        week: weekNum,
        month: date.month() + 1,
        days: []
      };
    }
    weeks[weekKey].days.push(date);
  });

  return weeks;
}

/**
 * 프로젝트 총 일수
 */
export function getTotalProjectDays(projectStart: Dayjs, projectEnd: Dayjs): number {
  return daysBetween(projectStart, projectEnd);
}

/**
 * 간트차트 전체 너비
 */
export function getGanttTotalWidth(projectStart: Dayjs, projectEnd: Dayjs): number {
  return getTotalProjectDays(projectStart, projectEnd) * DAY_WIDTH;
}
